import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Heart, X, ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Static parent photos from user uploads
const PARENT_PHOTOS = [
  {
    id: 'parent-1',
    url: 'https://customer-assets-jt897jd0.emergentagent.net/job_kitten-reserve-dev/artifacts/42293c5bc132de65_IMG_2468.jpeg',
    isMain: true,
  },
  {
    id: 'parent-2',
    url: 'https://customer-assets-jt897jd0.emergentagent.net/job_kitten-reserve-dev/artifacts/f0df0717a33ed5c8_IMG_2466.jpeg',
    isMain: true,
  },
  {
    id: 'photo-3',
    url: 'https://customer-assets-jt897jd0.emergentagent.net/job_kitten-reserve-dev/artifacts/6f962eb78d810a75_IMG_2473.jpeg',
    isMain: false,
  },
  {
    id: 'photo-4',
    url: 'https://customer-assets-jt897jd0.emergentagent.net/job_kitten-reserve-dev/artifacts/b7f2335eba3d45ab_IMG_2474.jpeg',
    isMain: false,
  },
  {
    id: 'photo-5',
    url: 'https://customer-assets-jt897jd0.emergentagent.net/job_kitten-reserve-dev/artifacts/0dfc9ce111b4e737_IMG_2470.jpeg',
    isMain: false,
  },
];

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get main parents (Angel and Thomas)
  const mainParents = PARENT_PHOTOS.filter(p => p.isMain);
  const galleryPhotos = PARENT_PHOTOS.filter(p => !p.isMain);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(PARENT_PHOTOS.findIndex(p => p.id === image.id));
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === 0 ? PARENT_PHOTOS.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(PARENT_PHOTOS[newIndex]);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === PARENT_PHOTOS.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(PARENT_PHOTOS[newIndex]);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious(e);
      if (e.key === 'ArrowRight') goToNext(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentIndex]);

  return (
    <main className="py-12 lg:py-20 min-h-screen bg-[#FAF8F5]">
      <SEO 
        title="Meet the Parents | KittenState Breeding Cats"
        description="Meet the Scottish Fold and Scottish Straight breeding cats at KittenState. Our parent cats are healthy, well-tempered, and produce beautiful kittens with wonderful personalities."
        keywords="Scottish Fold parents, Scottish Straight breeding cats, KittenState breeding cats, kitten parents"
        url="https://kittenstate.com/gallery"
      />
      
      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-10 lg:mb-16">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E85D75] mb-3">
            Our Family
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold tracking-tight leading-tight text-stone-800 mb-4 lg:mb-6">
            Meet the Parents
          </h1>
          <p className="text-base lg:text-lg leading-relaxed text-stone-600 max-w-2xl mx-auto">
            Our kittens come from loving, healthy parents with excellent temperaments. 
            Meet our beautiful breeding cats — the heart of our cattery.
          </p>
        </div>

        {/* Main Parents - Hero Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-12 lg:mb-16">
          {mainParents.map((parent) => (
            <div
              key={parent.id}
              className="group bg-white rounded-3xl overflow-hidden shadow-lg shadow-stone-200/50 hover:shadow-xl hover:shadow-[#E85D75]/10 transition-all duration-300 cursor-pointer"
              onClick={() => openLightbox(parent, 0)}
            >
              <div className="aspect-[4/5] overflow-hidden relative">
                <img
                  src={parent.url}
                  alt="Breeding parent"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {/* View indicator */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-sm font-medium bg-black/30 px-4 py-2 rounded-full backdrop-blur-sm">Click to enlarge</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Gallery Section */}
        <div className="mb-8">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
            {galleryPhotos.map((photo, index) => (
              <div
                key={photo.id}
                className="group cursor-pointer relative rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-lg transition-all duration-300"
                onClick={() => openLightbox(photo, index)}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={photo.url}
                    alt="Cat photo"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                {/* Hover overlay - just zoom effect, no text */}
                <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-white rounded-3xl p-6 lg:p-10 text-center shadow-lg shadow-stone-200/50 border border-rose-100/50">
          <h3 className="text-2xl lg:text-3xl font-serif font-medium text-stone-800 mb-3">
            Want to Meet Our Kittens?
          </h3>
          <p className="text-stone-600 mb-6 max-w-lg mx-auto">
            Schedule a video call to meet our adorable kittens and see them interact with their parents.
          </p>
          <a
            href="/kittens"
            className="inline-flex items-center gap-2 bg-[#E85D75] text-white hover:bg-[#D64560] px-8 py-4 rounded-full font-medium shadow-lg shadow-[#E85D75]/25 transition-all hover:scale-105 active:scale-95"
          >
            <Heart className="w-4 h-4" />
            View Available Kittens
          </a>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Navigation arrows */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
            onClick={goToPrevious}
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
            onClick={goToNext}
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Image container */}
          <div 
            className="max-w-5xl max-h-[85vh] px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.url}
              alt="Cat photo"
              className="max-w-full max-h-[85vh] object-contain rounded-2xl mx-auto"
            />
            <div className="text-center mt-4">
              <p className="text-white/50 text-sm">
                {currentIndex + 1} / {PARENT_PHOTOS.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
